import { z } from 'zod';
import { resourceSavedViewSchema } from './resources.js';

export * from './announcements.js';
export * from './communications.js';
export * from './resources.js';
export * from './user-preferences.js';

export const tenantRoleSchema = z.enum([
  'platform_admin',
  'tenant_admin',
  'manager',
  'team_leader',
  'standard_user',
  // Compatibility values for memberships created before the organisation role model.
  'quality_manager',
  'auditor',
  'viewer',
]);

export type TenantRole = z.infer<typeof tenantRoleSchema>;

export const tenantStatusSchema = z.enum(['onboarding', 'active', 'suspended', 'archived']);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

export const tenantOnboardingStatusSchema = z.enum([
  'not_started',
  'in_progress',
  'ready',
  'complete',
]);
export type TenantOnboardingStatus = z.infer<typeof tenantOnboardingStatusSchema>;

export const tenantOffboardingStatusSchema = z.enum(['none', 'requested', 'export_ready']);
export type TenantOffboardingStatus = z.infer<typeof tenantOffboardingStatusSchema>;

export const tenantOnboardingChecklistSchema = z.object({
  organisationDetailsConfirmed: z.boolean(),
  primaryAdministratorInvited: z.boolean(),
  dataRegionConfirmed: z.boolean(),
  providerSelected: z.boolean().optional(),
  primaryAdministratorAssigned: z.boolean().optional(),
  providerVerificationRequested: z.boolean().optional(),
  providerVerified: z.boolean().optional(),
});
export type TenantOnboardingChecklist = z.infer<typeof tenantOnboardingChecklistSchema>;

export const providerVerificationStatusSchema = z.enum([
  'pending',
  'verified',
  'manual_review_required',
]);
export type ProviderVerificationStatus = z.infer<typeof providerVerificationStatusSchema>;

export const providerVerificationMethodSchema = z.enum(['tga_ceo_email', 'platform_manual']);
export type ProviderVerificationMethod = z.infer<typeof providerVerificationMethodSchema>;

export const organisationAddressSchema = z.object({
  addressLines: z.array(z.string().trim().min(1).max(200)).min(1).max(5),
  suburb: z.string().trim().min(1).max(100),
  state: z.string().trim().min(1).max(100),
  postcode: z.string().trim().min(3).max(12),
  country: z.string().trim().min(2).max(100),
});
export type OrganisationAddress = z.infer<typeof organisationAddressSchema>;

export const organisationLocationSchema = organisationAddressSchema.extend({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(160),
  code: z.string().trim().min(1).max(40).optional(),
  description: z.string().trim().max(2_000).optional(),
  contactEmail: z.email().optional(),
  phone: z.string().trim().min(3).max(40).optional(),
  isPrimary: z.boolean().optional(),
  status: z.enum(['active', 'archived']).optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});
export type OrganisationLocation = z.infer<typeof organisationLocationSchema>;

export const organisationTeamSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(2_000).optional(),
  locationIds: z.array(z.string().uuid()).max(100).default([]),
  managerUserIds: z.array(z.string().min(1)).max(100).optional(),
  archivedManagerUserIds: z.array(z.string().min(1)).max(100).optional(),
  status: z.enum(['active', 'archived']).optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});
export type OrganisationTeam = z.infer<typeof organisationTeamSchema>;

export const organisationStructureSchema = z
  .object({
    locations: z.array(organisationLocationSchema).max(100),
    teams: z.array(organisationTeamSchema).max(100),
  })
  .superRefine((value, context) => {
    const locationIds = value.locations.map((location) => location.id);
    const teamIds = value.teams.map((team) => team.id);
    if (new Set(locationIds).size !== locationIds.length) {
      context.addIssue({ code: 'custom', message: 'Location identifiers must be unique' });
    }
    if (new Set(teamIds).size !== teamIds.length) {
      context.addIssue({ code: 'custom', message: 'Team identifiers must be unique' });
    }
    if (value.locations.filter((location) => location.isPrimary === true).length > 1) {
      context.addIssue({ code: 'custom', message: 'Only one location can be primary' });
    }
    const locationCodes = value.locations
      .map((location) => location.code?.toLowerCase())
      .filter((code): code is string => Boolean(code));
    if (new Set(locationCodes).size !== locationCodes.length) {
      context.addIssue({ code: 'custom', message: 'Location codes must be unique' });
    }
    const knownLocations = new Set(locationIds);
    for (const team of value.teams) {
      if (team.locationIds.some((locationId) => !knownLocations.has(locationId))) {
        context.addIssue({ code: 'custom', message: 'Teams can only reference tenant locations' });
      }
      if (new Set(team.managerUserIds ?? []).size !== (team.managerUserIds ?? []).length) {
        context.addIssue({ code: 'custom', message: 'Team manager identifiers must be unique' });
      }
    }
  });
export type OrganisationStructure = z.infer<typeof organisationStructureSchema>;

export const organisationProfileSchema = z.object({
  legalName: z.string().trim().min(1).max(160).optional(),
  tradingName: z.string().trim().min(1).max(160).optional(),
  description: z.string().trim().max(4_000).optional(),
  contactEmail: z.email().optional(),
  phone: z.string().trim().min(3).max(40).optional(),
  website: z.url().max(500).optional(),
  address: organisationAddressSchema.optional(),
  brandColour: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  logoFileId: z.string().min(1).max(200).optional(),
});
export type OrganisationProfile = z.infer<typeof organisationProfileSchema>;

export const resourceReminderDefaultsSchema = z
  .object({
    reminderDays: z.array(z.number().int().min(0).max(730)).min(1).max(10).default([30, 14, 7, 0]),
    escalationPersonId: z.string().min(1).max(200).optional(),
    weeklyDigestEnabled: z.boolean().default(true),
  })
  .superRefine((value, context) => {
    if (new Set(value.reminderDays).size !== value.reminderDays.length) {
      context.addIssue({
        code: 'custom',
        path: ['reminderDays'],
        message: 'Reminder days must be unique',
      });
    }
  });
export type ResourceReminderDefaults = z.infer<typeof resourceReminderDefaultsSchema>;

export const tenantSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  name: z.string().min(1).max(160),
  status: tenantStatusSchema,
  mfaRequired: z.boolean().optional(),
  legalName: organisationProfileSchema.shape.legalName,
  tradingName: organisationProfileSchema.shape.tradingName,
  description: organisationProfileSchema.shape.description,
  contactEmail: organisationProfileSchema.shape.contactEmail,
  phone: organisationProfileSchema.shape.phone,
  website: organisationProfileSchema.shape.website,
  address: organisationProfileSchema.shape.address,
  brandColour: organisationProfileSchema.shape.brandColour,
  logoFileId: organisationProfileSchema.shape.logoFileId,
  resourceReminderDefaults: resourceReminderDefaultsSchema.optional(),
  resourceSavedViews: z.array(resourceSavedViewSchema).max(100).optional(),
  locations: organisationStructureSchema.shape.locations.optional(),
  teams: organisationStructureSchema.shape.teams.optional(),
  onboardingStatus: tenantOnboardingStatusSchema.optional(),
  onboardingChecklist: tenantOnboardingChecklistSchema.optional(),
  offboardingStatus: tenantOffboardingStatusSchema.optional(),
  offboardingReason: z.string().trim().min(1).max(2_000).optional(),
  offboardingRequestedAt: z.string().datetime().optional(),
  suspendedAt: z.string().datetime().optional(),
  billingContactName: z.string().trim().min(1).max(160).optional(),
  billingEmail: z.email().optional(),
  billingAddressLines: z.array(z.string().trim().min(1).max(200)).min(1).max(5).optional(),
  subscriptionAccessUntil: z.string().datetime().optional(),
  tgaProviderCode: z.string().trim().min(1).max(32).optional(),
  providerVerificationStatus: providerVerificationStatusSchema.optional(),
  providerVerifiedAt: z.string().datetime().optional(),
  providerVerificationMethod: providerVerificationMethodSchema.optional(),
  deletionRequestedAt: z.string().datetime().optional(),
  deletionRequestedBy: z.string().min(1).optional(),
  deletionRetentionDueAt: z.string().datetime().optional(),
  deletionRetentionState: z.enum(['pending_retention_execution', 'ready_for_execution']).optional(),
  deletionRetentionReadiedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});

export type Tenant = z.infer<typeof tenantSchema>;

export const createTenantSchema = z.object({
  slug: tenantSchema.shape.slug,
  name: tenantSchema.shape.name,
  administratorEmail: z.email(),
});

export const updateTenantLifecycleSchema = z
  .object({
    name: tenantSchema.shape.name.optional(),
    status: tenantStatusSchema.optional(),
    onboardingStatus: tenantOnboardingStatusSchema.optional(),
    onboardingChecklist: tenantOnboardingChecklistSchema.optional(),
    offboardingStatus: tenantOffboardingStatusSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated');

export const requestTenantDeletionSchema = z.object({
  confirmationName: z.string().trim().min(1).max(160),
});

export const updateTenantMfaPolicySchema = z.object({
  mfaRequired: z.boolean(),
});

export const updateOrganisationStructureSchema = organisationStructureSchema;

export const updateOrganisationProfileSchema = organisationProfileSchema
  .partial()
  .extend({ logoFileId: organisationProfileSchema.shape.logoFileId.unwrap().nullable().optional() })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated');

export const organisationLogoUploadSchema = z
  .object({
    fileName: z.string().trim().min(1).max(200),
    contentType: z.enum(['image/png', 'image/jpeg', 'image/webp']),
  })
  .strict();

export const createOrganisationLocationSchema = organisationLocationSchema.omit({
  id: true,
  status: true,
  createdAt: true,
  updatedAt: true,
});
export const updateOrganisationLocationSchema = createOrganisationLocationSchema
  .partial()
  .extend({
    status: z.enum(['active', 'archived']).optional(),
    resourceImpactDecision: z.enum(['review_required', 'reassigned']).optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated');

export const createOrganisationTeamSchema = organisationTeamSchema.omit({
  id: true,
  status: true,
  createdAt: true,
  updatedAt: true,
  archivedManagerUserIds: true,
});
export const updateOrganisationTeamSchema = createOrganisationTeamSchema
  .partial()
  .extend({ status: z.enum(['active', 'archived']).optional() })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated');

export const requestTenantOffboardingSchema = z.object({
  reason: z.string().trim().min(10).max(2_000),
});

export const updateTenantBillingProfileSchema = z.object({
  contactName: z.string().trim().min(1).max(160),
  email: z.email(),
  addressLines: z.array(z.string().trim().min(1).max(200)).min(1).max(5),
});

export const tenantRetentionPolicySchema = z.object({
  tenantId: z.string().uuid(),
  qualityRecordsDays: z.number().int().min(365).max(3_650),
  surveyResponsesDays: z.number().int().min(30).max(2_555),
  filesDays: z.number().int().min(30).max(3_650),
  supportCasesDays: z.number().int().min(30).max(2_555),
  auditEventsDays: z.number().int().min(365).max(3_650),
  executionStatus: z.literal('disabled_pending_approval'),
  updatedBy: z.string().min(1),
  updatedAt: z.string().datetime(),
});
export type TenantRetentionPolicy = z.infer<typeof tenantRetentionPolicySchema>;

export const updateTenantRetentionPolicySchema = tenantRetentionPolicySchema.pick({
  qualityRecordsDays: true,
  surveyResponsesDays: true,
  filesDays: true,
  supportCasesDays: true,
  auditEventsDays: true,
});

export const legalHoldScopeSchema = z.enum([
  'all_tenant_data',
  'quality_records',
  'survey_responses',
  'files',
  'support_cases',
  'audit_events',
]);
export type LegalHoldScope = z.infer<typeof legalHoldScopeSchema>;

export const legalHoldSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  scope: legalHoldScopeSchema,
  reason: z.string().trim().min(10).max(2_000),
  reference: z.string().trim().min(1).max(200).optional(),
  status: z.enum(['active', 'released']),
  createdBy: z.string().min(1),
  createdAt: z.string().datetime(),
  releasedBy: z.string().min(1).optional(),
  releasedAt: z.string().datetime().optional(),
  releaseReason: z.string().trim().min(10).max(2_000).optional(),
});
export type LegalHold = z.infer<typeof legalHoldSchema>;

export const createLegalHoldSchema = legalHoldSchema.pick({
  scope: true,
  reason: true,
  reference: true,
});

export const releaseLegalHoldSchema = z.object({
  releaseReason: z.string().trim().min(10).max(2_000),
});

export const subscriptionSourceSchema = z.enum(['stripe', 'gift']);
export type SubscriptionSource = z.infer<typeof subscriptionSourceSchema>;

export const subscriptionStatusSchema = z.enum([
  'pending_payment',
  'active',
  'trialing',
  'past_due',
  'cancelled',
  'expired',
  'failed',
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

export const stripeSubscriptionEventOrderSchema = z.object({
  createdAt: z.string().datetime(),
  precedence: z.number().int().nonnegative(),
  eventId: z.string().min(1),
  eventType: z.string().min(1).max(200),
});
export type StripeSubscriptionEventOrder = z.infer<typeof stripeSubscriptionEventOrderSchema>;

export const organisationSubscriptionSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  planCode: z.literal('organisation'),
  source: subscriptionSourceSchema,
  status: subscriptionStatusSchema,
  accessStartsAt: z.string().datetime(),
  accessEndsAt: z.string().datetime(),
  giftedBy: z.string().min(1).optional(),
  giftReason: z.string().trim().min(1).max(2_000).optional(),
  stripeCheckoutSessionId: z.string().min(1).optional(),
  stripeCustomerId: z.string().min(1).optional(),
  stripeSubscriptionId: z.string().min(1).optional(),
  stripeEventOrder: stripeSubscriptionEventOrderSchema.optional(),
  cancelAtPeriodEnd: z.boolean().default(false),
  createdBy: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type OrganisationSubscription = z.infer<typeof organisationSubscriptionSchema>;

export const giftOrganisationSubscriptionSchema = z.object({
  durationDays: z.number().int().min(1).max(1_095),
  reason: z.string().trim().min(10).max(2_000),
});

export const applicationInvoiceStatusSchema = z.enum(['issued', 'paid', 'void']);
export type ApplicationInvoiceStatus = z.infer<typeof applicationInvoiceStatusSchema>;

export const applicationInvoiceLineSchema = z.object({
  description: z.string().trim().min(1).max(500),
  quantity: z.number().int().positive(),
  subtotalCents: z.number().int().nonnegative(),
  gstCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
});
export type ApplicationInvoiceLine = z.infer<typeof applicationInvoiceLineSchema>;

export const applicationInvoiceSchema = z.object({
  id: z.string().min(1),
  invoiceNumber: z.string().regex(/^QV-\d{4}-\d{6}$/),
  tenantId: z.string().uuid(),
  subscriptionId: z.string().min(1),
  status: applicationInvoiceStatusSchema,
  currency: z.literal('AUD'),
  sellerName: z.string().trim().min(1).max(200),
  sellerAbn: z.string().regex(/^\d{11}$/),
  sellerGstRegistered: z.boolean(),
  buyerName: z.string().trim().min(1).max(200),
  buyerEmail: z.email(),
  buyerAddressLines: z.array(z.string().trim().min(1).max(200)).min(1).max(5),
  lines: z.array(applicationInvoiceLineSchema).min(1).max(20),
  subtotalCents: z.number().int().nonnegative(),
  gstCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  stripeInvoiceId: z.string().min(1),
  stripePaymentReference: z.string().min(1).optional(),
  issuedAt: z.string().datetime(),
  paidAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
});
export type ApplicationInvoice = z.infer<typeof applicationInvoiceSchema>;

export const supportCasePrioritySchema = z.enum(['normal', 'high']);
export type SupportCasePriority = z.infer<typeof supportCasePrioritySchema>;

export const supportCaseStatusSchema = z.enum(['open', 'in_progress', 'resolved', 'closed']);
export type SupportCaseStatus = z.infer<typeof supportCaseStatusSchema>;

export const supportCaseSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  subject: z.string().trim().min(5).max(160),
  description: z.string().trim().min(10).max(4_000),
  priority: supportCasePrioritySchema,
  status: supportCaseStatusSchema,
  createdBy: z.string().min(1),
  latestResponse: z.string().trim().min(10).max(4_000).optional(),
  respondedBy: z.string().min(1).optional(),
  respondedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SupportCase = z.infer<typeof supportCaseSchema>;

export const createSupportCaseSchema = supportCaseSchema.pick({
  subject: true,
  description: true,
  priority: true,
});

export const updateSupportCaseSchema = z
  .object({
    status: supportCaseStatusSchema.optional(),
    response: z.string().trim().min(10).max(4_000).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated');

export const membershipSchema = z.object({
  tenantId: z.string().uuid(),
  userId: z.string().min(1),
  email: z.email(),
  displayName: z.string().trim().min(1).max(160).optional(),
  roles: z.array(tenantRoleSchema).min(1),
  locationIds: z.array(z.string().uuid()).max(100).optional(),
  teamIds: z.array(z.string().uuid()).max(100).optional(),
  teamLeaderTeamIds: z.array(z.string().uuid()).max(100).optional(),
  managerUserIds: z.array(z.string().min(1)).max(100).optional(),
  archivedLocationIds: z.array(z.string().uuid()).max(100).optional(),
  archivedTeamIds: z.array(z.string().uuid()).max(100).optional(),
  archivedTeamLeaderTeamIds: z.array(z.string().uuid()).max(100).optional(),
  archivedManagerUserIds: z.array(z.string().min(1)).max(100).optional(),
  assignmentsArchivedAt: z.string().datetime().optional(),
  archiveRequestId: z.string().uuid().optional(),
  archiveRequestStatus: z.enum(['pending', 'approved', 'rejected', 'cancelled']).optional(),
  jobTitle: z.string().trim().min(1).max(160).optional(),
  status: z.enum(['invited', 'active', 'disabled', 'archive_requested', 'archived']),
  archiveRequestedAt: z.string().datetime().optional(),
  archiveRequestedBy: z.string().min(1).optional(),
  archiveReason: z.string().trim().min(1).max(2_000).optional(),
  archivedAt: z.string().datetime().optional(),
  archivedBy: z.string().min(1).optional(),
  deletedAt: z.string().datetime().optional(),
  deletedBy: z.string().min(1).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});

export type Membership = z.infer<typeof membershipSchema>;

export const membershipArchiveRequestSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().min(1),
  requestedBy: z.string().min(1),
  reason: z.string().trim().min(3).max(2_000),
  status: z.enum(['pending', 'approved', 'rejected', 'cancelled']),
  requestedAt: z.string().datetime(),
  decidedAt: z.string().datetime().optional(),
  decidedBy: z.string().min(1).optional(),
  decisionReason: z.string().trim().min(3).max(2_000).optional(),
});
export type MembershipArchiveRequest = z.infer<typeof membershipArchiveRequestSchema>;

export const membershipAccessRequestSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  providerCode: z.string().trim().min(1).max(32),
  requesterUserId: z.string().min(1),
  requesterEmail: z.email(),
  status: z.enum(['pending', 'approved', 'rejected']),
  requestedAt: z.string().datetime(),
  decidedAt: z.string().datetime().optional(),
  decidedBy: z.string().min(1).optional(),
});
export type MembershipAccessRequest = z.infer<typeof membershipAccessRequestSchema>;

export const requestContextSchema = z.object({
  requestId: z.string().min(1),
  userId: z.string().min(1),
  email: z.email().optional(),
  tenantId: z.string().uuid().optional(),
  roles: z.array(tenantRoleSchema),
  isPlatformAdmin: z.boolean(),
  legacyRoleDetected: z.boolean().optional(),
});

export type RequestContext = z.infer<typeof requestContextSchema>;

export const auditEventSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  actorId: z.string().min(1),
  action: z.string().min(1).max(120),
  targetType: z.string().min(1).max(80),
  targetId: z.string().min(1).max(200),
  outcome: z.enum(['allowed', 'denied', 'failed']),
  occurredAt: z.string().datetime(),
  requestId: z.string().min(1),
  metadata: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
});

export type AuditEvent = z.infer<typeof auditEventSchema>;

const notificationDataSchema = z
  .record(z.string().min(1).max(40), z.string().max(2_000))
  .refine((data) => Object.keys(data).length <= 20, 'Notification data has too many fields');

export const notificationRequestedSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid().optional(),
  template: z.enum([
    'account-invitation',
    'quality-reminder',
    'survey-invitation',
    'invoice-issued',
    'provider-verification',
    'membership-access-request',
    'feature-suggestion',
  ]),
  recipient: z.email(),
  requestedBy: z.string().min(1),
  requestedAt: z.string().datetime(),
  data: notificationDataSchema,
});

export type NotificationRequested = z.infer<typeof notificationRequestedSchema>;

export const serviceTermsAcceptanceSchema = z.object({
  id: z.string().min(1).max(100),
  tenantId: z.string().uuid(),
  userId: z.string().min(1).max(200),
  termsVersion: z.string().min(1).max(40),
  termsHash: z.string().regex(/^[a-f0-9]{64}$/),
  acceptedAt: z.string().datetime(),
  requestId: z.string().min(1).max(200),
  evidenceVersion: z.literal(1),
});
export type ServiceTermsAcceptance = z.infer<typeof serviceTermsAcceptanceSchema>;

export const acceptServiceTermsRequestSchema = z.object({
  termsVersion: z.string().min(1).max(40),
  termsHash: z.string().regex(/^[a-f0-9]{64}$/),
  confirmed: z.literal(true),
});
export type AcceptServiceTermsRequest = z.infer<typeof acceptServiceTermsRequestSchema>;

const selfSignupEmailSchema = z.string().trim().max(254).pipe(z.email());

export const selfSignupRequestSchema = z.object({
  email: selfSignupEmailSchema,
  organisationName: z.string().trim().min(2).max(160),
  termsVersion: z.string().min(1).max(40),
  termsHash: z.string().regex(/^[a-f0-9]{64}$/),
  confirmed: z.literal(true),
  clientRequestId: z
    .string()
    .trim()
    .min(16)
    .max(100)
    .regex(/^[A-Za-z0-9_-]+$/),
});
export type SelfSignupRequest = z.infer<typeof selfSignupRequestSchema>;

export const selfSignupRecordSchema = z.object({
  signupId: z.string().min(1).max(100),
  tenantId: z.string().uuid(),
  userId: z.string().min(1).max(200).optional(),
  email: selfSignupEmailSchema,
  organisationName: z.string().trim().min(2).max(160),
  tenantSlug: tenantSchema.shape.slug,
  termsVersion: z.string().min(1).max(40),
  termsHash: z.string().regex(/^[a-f0-9]{64}$/),
  clientRequestId: selfSignupRequestSchema.shape.clientRequestId,
  signupRequestId: z.string().min(1).max(200),
  status: z.enum(['reserved', 'invited', 'provisioned']),
  requestedAt: z.string().datetime(),
  provisionedAt: z.string().datetime().optional(),
});
export type SelfSignupRecord = z.infer<typeof selfSignupRecordSchema>;

export const selfSignupResponseSchema = z.object({
  accepted: z.literal(true),
  delivery: z.object({ medium: z.literal('EMAIL') }),
});
export type SelfSignupResponse = z.infer<typeof selfSignupResponseSchema>;

export const featureSuggestionRequestSchema = z.object({
  title: z.string().trim().max(200).default(''),
  description: z.string().trim().min(1).max(2_000),
  submitterName: z.string().trim().min(1).max(160).optional(),
  clientRequestId: z
    .string()
    .trim()
    .min(10)
    .max(100)
    .regex(/^[A-Za-z0-9_-]+$/)
    .optional(),
});
export type FeatureSuggestionRequest = z.infer<typeof featureSuggestionRequestSchema>;

export const featureSuggestionRecordSchema = z.object({
  id: z.string().min(1).max(100),
  requestedBy: z.string().min(1).max(200),
  submitterEmail: z.email().optional(),
  requestedAt: z.string().datetime(),
  title: z.string().min(1).max(200),
  description: z.string().min(1).max(2_000),
  requestId: z.string().min(1).max(200),
  clientRequestIdHash: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
  deliveryStatus: z.enum(['pending', 'queued', 'failed']),
  retentionExpiresAt: z.string().datetime(),
  deletedAt: z.string().datetime().optional(),
  deletedBy: z.string().min(1).max(200).optional(),
});
export type FeatureSuggestionRecord = z.infer<typeof featureSuggestionRecordSchema>;

export const tgaCacheFreshnessSchema = z.object({
  source: z.literal('training.gov.au'),
  sourceUpdatedAt: z.string().datetime().nullable(),
  cachedAt: z.string().datetime(),
  isStale: z.boolean(),
  staleReason: z.string().nullable(),
  syncRunId: z.string().optional(),
  schemaVersion: z.number().int().positive(),
});
export type TgaCacheFreshness = z.infer<typeof tgaCacheFreshnessSchema>;

export const tgaProviderContactSchema = z
  .object({
    id: z.string().min(1),
    roleCode: z.string().optional(),
    roleName: z.string().optional(),
    typeCode: z.string().optional(),
    title: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    jobTitle: z.string().optional(),
    groupName: z.string().optional(),
    organisationName: z.string().optional(),
    email: z.email().optional(),
    phone: z.string().optional(),
    mobile: z.string().optional(),
    fax: z.string().optional(),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    isCurrent: z.boolean(),
    isChiefExecutive: z.boolean(),
  })
  .passthrough();
export type TgaProviderContact = z.infer<typeof tgaProviderContactSchema>;

export const tgaProviderSummarySchema = z.object({
  providerCode: z.string().min(1).max(32),
  legalName: z.string().min(1).max(500),
  tradingNames: z.array(z.string()).default([]),
  status: z.string().min(1),
  isCurrentRto: z.boolean(),
  registrationStartDate: z.string().datetime().optional(),
  registrationEndDate: z.string().datetime().optional(),
  abn: z.string().optional(),
  freshness: tgaCacheFreshnessSchema,
});
export type TgaProviderSummary = z.infer<typeof tgaProviderSummarySchema>;

export const tgaProviderDetailsSchema = tgaProviderSummarySchema
  .extend({
    contacts: z.array(tgaProviderContactSchema),
    addresses: z.array(z.record(z.string(), z.unknown())),
    registrationPeriods: z.array(z.record(z.string(), z.unknown())),
    restrictions: z.array(z.record(z.string(), z.unknown())),
    regulatoryDecisions: z.array(z.record(z.string(), z.unknown())),
    classifications: z.array(z.record(z.string(), z.unknown())),
    roles: z.array(z.record(z.string(), z.unknown())),
    urls: z.array(z.record(z.string(), z.unknown())),
    sourcePayloadKey: z.string().optional(),
    sourcePayloadHash: z.string().min(1),
  })
  .passthrough();
export type TgaProviderDetails = z.infer<typeof tgaProviderDetailsSchema>;

export const tgaProviderScopeItemSchema = z.object({
  providerCode: z.string().min(1).max(32),
  nationalCode: z.string().min(1).max(64),
  title: z.string().default(''),
  trainingComponentType: z.string().default('Unknown'),
  extentCode: z.string().optional(),
  scopeDecisionType: z.string().optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  isImplicit: z.boolean(),
  isRefused: z.boolean(),
  isCurrent: z.boolean(),
  freshness: tgaCacheFreshnessSchema,
});
export type TgaProviderScopeItem = z.infer<typeof tgaProviderScopeItemSchema>;

export const tgaTrainingProductDetailsSchema = z
  .object({
    nationalCode: z.string().min(1).max(64),
    title: z.string().min(1),
    componentType: z.string().min(1),
    status: z.string().min(1),
    releaseNumber: z.string().optional(),
    sourcePayloadKey: z.string().optional(),
    sourcePayloadHash: z.string().min(1),
    structuredContent: z.record(z.string(), z.unknown()),
    freshness: tgaCacheFreshnessSchema,
  })
  .passthrough();
export type TgaTrainingProductDetails = z.infer<typeof tgaTrainingProductDetailsSchema>;

export const providerVerificationSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  providerCode: z.string().min(1).max(32),
  providerName: z.string().min(1),
  requesterUserId: z.string().min(1),
  requesterEmail: z.email(),
  status: providerVerificationStatusSchema,
  expiresAt: z.string().datetime().optional(),
  requestedAt: z.string().datetime(),
  lastSentAt: z.string().datetime().optional(),
  verifiedAt: z.string().datetime().optional(),
  verificationMethod: providerVerificationMethodSchema.optional(),
});
export type ProviderVerification = z.infer<typeof providerVerificationSchema>;

export const selectTgaProviderSchema = z.object({
  providerCode: z.string().trim().min(1).max(32),
});

export const providerVerificationTokenSchema = z.object({
  token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});

export const resendProviderVerificationSchema = z.object({
  tenantId: z.string().uuid(),
});

export const manualProviderVerificationSchema = z.object({
  reason: z.string().trim().min(10).max(2_000),
});

export const decideMembershipAccessRequestSchema = z.object({
  requestId: z.string().min(1),
  decision: z.enum(['approved', 'rejected']),
});
export const surveyAccessTokenSchema = z
  .string()
  .length(64)
  .transform((value) => value.toLowerCase())
  .pipe(z.string().regex(/^[0-9a-f]{64}$/));

export const createNotificationSchema = z
  .object({
    template: z.literal('survey-invitation'),
    recipient: z.email(),
    data: z.object({ surveyToken: surveyAccessTokenSchema }).strict(),
  })
  .strict();

export const aiJobRequestedSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  requestedBy: z.string().min(1),
  useCaseId: z.literal('QV-AI-DEV-001'),
  purpose: z.enum(['smoke_test', 'summarise', 'classify']),
  prompt: z.string().min(1).max(8_000),
  dataDeclaration: z.literal('synthetic_or_non_personal_only'),
  humanReviewAcknowledged: z.literal(true),
  requestedAt: z.string().datetime(),
});

export type AiJobRequested = z.infer<typeof aiJobRequestedSchema>;

export const demoRecordSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  title: z.string().min(1).max(200),
  status: z.enum(['draft', 'active', 'complete']),
  createdBy: z.string().min(1),
  createdAt: z.string().datetime(),
});

export type DemoRecord = z.infer<typeof demoRecordSchema>;

export const createDemoRecordSchema = demoRecordSchema.pick({ title: true });
export const tenantAssignableRoleSchema = z.enum([
  'tenant_admin',
  'manager',
  'team_leader',
  'standard_user',
]);
export const createMembershipSchema = z.object({
  email: z.email(),
  displayName: z.string().trim().min(1).max(160).optional(),
  roles: z.array(tenantAssignableRoleSchema).min(1),
  locationIds: z.array(z.string().uuid()).max(100).optional(),
  teamIds: z.array(z.string().uuid()).max(100).optional(),
  teamLeaderTeamIds: z.array(z.string().uuid()).max(100).optional(),
  managerUserIds: z.array(z.string().min(1)).max(100).optional(),
  jobTitle: z.string().trim().min(1).max(160).optional(),
});
export const updateMembershipSchema = z
  .object({
    displayName: z.string().trim().min(1).max(160).optional(),
    roles: z.array(tenantAssignableRoleSchema).min(1).optional(),
    status: z.enum(['active', 'disabled']).optional(),
    locationIds: z.array(z.string().uuid()).max(100).optional(),
    teamIds: z.array(z.string().uuid()).max(100).optional(),
    teamLeaderTeamIds: z.array(z.string().uuid()).max(100).optional(),
    managerUserIds: z.array(z.string().min(1)).max(100).optional(),
    jobTitle: z.string().trim().min(1).max(160).optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated');

export const requestMembershipArchiveSchema = z.object({
  reason: z.string().trim().min(3).max(2_000),
});

export const decideMembershipArchiveSchema = z.object({
  decisionReason: z.string().trim().min(3).max(2_000).optional(),
  resourceImpactDecision: z.enum(['review_required', 'reassigned']).optional(),
});

export const controlledMembershipDeletionSchema = z.object({
  confirmationEmail: z.email(),
});

export const qualityItemKindSchema = z.enum(['plan', 'schedule', 'audit', 'survey']);
export type QualityItemKind = z.infer<typeof qualityItemKindSchema>;

export const qualityItemStatusSchema = z.enum([
  'draft',
  'scheduled',
  'in_progress',
  'complete',
  'cancelled',
]);
export type QualityItemStatus = z.infer<typeof qualityItemStatusSchema>;

export const qualitySummarySchema = z.object({
  tenantId: z.string().uuid(),
  generatedAt: z.string().datetime(),
  horizonDays: z.number().int().min(7).max(365),
  total: z.number().int().nonnegative(),
  byKind: z.record(qualityItemKindSchema, z.number().int().nonnegative()),
  byStatus: z.record(qualityItemStatusSchema, z.number().int().nonnegative()),
  overdue: z.number().int().nonnegative(),
  overdueByKind: z.record(qualityItemKindSchema, z.number().int().nonnegative()),
  dueSoon: z.number().int().nonnegative(),
  dueSoonByKind: z.record(qualityItemKindSchema, z.number().int().nonnegative()),
  completionRatePercent: z.number().min(0).max(100),
});
export type QualitySummary = z.infer<typeof qualitySummarySchema>;

export const qualitySummaryQuerySchema = z.object({
  horizonDays: z.coerce.number().int().min(7).max(365).default(30),
});

export const planDetailsSchema = z.object({
  obligation: z.string().trim().min(1).max(500),
  objective: z.string().trim().min(1).max(2_000),
  successMeasures: z.array(z.string().trim().min(1).max(500)).min(1).max(10),
});
export type PlanDetails = z.infer<typeof planDetailsSchema>;

export const planApprovalStatusSchema = z.enum([
  'not_submitted',
  'pending',
  'approved',
  'changes_requested',
]);
export type PlanApprovalStatus = z.infer<typeof planApprovalStatusSchema>;

export const planApprovalSchema = z.object({
  status: planApprovalStatusSchema,
  submittedBy: z.string().min(1).optional(),
  submittedAt: z.string().datetime().optional(),
  decidedBy: z.string().min(1).optional(),
  decidedAt: z.string().datetime().optional(),
  comment: z.string().trim().min(1).max(2_000).optional(),
});
export type PlanApproval = z.infer<typeof planApprovalSchema>;

export const updatePlanApprovalSchema = z.object({
  action: z.enum(['submit', 'approve', 'request_changes']),
  comment: z.string().trim().min(1).max(2_000).optional(),
});

export const reopenPlanSchema = z.object({
  reason: z.string().trim().min(1).max(2_000),
});

export const scheduleDetailsSchema = z.object({
  activity: z.string().trim().min(1).max(500),
  cadence: z.enum(['one_off', 'monthly', 'quarterly', 'biannual', 'annual']),
  timezone: z.literal('Australia/Sydney').default('Australia/Sydney'),
  relatedPlanId: z.string().min(1).max(200).optional(),
});
export type ScheduleDetails = z.infer<typeof scheduleDetailsSchema>;

export const scheduleOccurrenceStatusSchema = z.enum([
  'pending',
  'in_progress',
  'complete',
  'skipped',
]);
export type ScheduleOccurrenceStatus = z.infer<typeof scheduleOccurrenceStatusSchema>;

export const scheduleOccurrenceSkipReasonSchema = z.enum([
  'not_required',
  'duplicate',
  'superseded',
  'unavailable',
  'other',
]);
export type ScheduleOccurrenceSkipReason = z.infer<typeof scheduleOccurrenceSkipReasonSchema>;

export const scheduleOccurrenceSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  scheduleId: z.string().min(1),
  sequence: z.number().int().positive(),
  scheduledFor: z.string().datetime(),
  recurrenceAnchorAt: z.string().datetime().optional(),
  timezone: z.literal('Australia/Sydney'),
  status: scheduleOccurrenceStatusSchema,
  ownerId: z.string().min(1),
  evidenceFileIds: z.array(z.string().min(1)).max(20).default([]),
  resolutionSummary: z.string().trim().min(1).max(4_000).optional(),
  skipReason: scheduleOccurrenceSkipReasonSchema.optional(),
  skipReasonDetails: z.string().trim().min(1).max(2_000).optional(),
  generatedFromId: z.string().min(1).optional(),
  createdBy: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ScheduleOccurrence = z.infer<typeof scheduleOccurrenceSchema>;

export const updateScheduleOccurrenceSchema = scheduleOccurrenceSchema
  .pick({
    status: true,
    scheduledFor: true,
    ownerId: true,
    evidenceFileIds: true,
    resolutionSummary: true,
    skipReason: true,
    skipReasonDetails: true,
  })
  .partial()
  .extend({
    rescheduleReason: z.string().trim().min(1).max(2_000).optional(),
  })
  .superRefine((value, context) => {
    if (Object.keys(value).length === 0) {
      context.addIssue({ code: 'custom', message: 'At least one field must be updated' });
    }
    if (Boolean(value.scheduledFor) !== Boolean(value.rescheduleReason)) {
      context.addIssue({
        code: 'custom',
        path: [value.scheduledFor ? 'rescheduleReason' : 'scheduledFor'],
        message: 'A new date and reschedule reason must be provided together',
      });
    }
    if (value.skipReason === 'other' && !value.skipReasonDetails) {
      context.addIssue({
        code: 'custom',
        path: ['skipReasonDetails'],
        message: 'Skip reason details are required when other is selected',
      });
    }
  });

export const auditDetailsSchema = z.object({
  scope: z.string().trim().min(1).max(2_000),
  criteria: z.array(z.string().trim().min(1).max(500)).min(1).max(20),
  sampling: z.string().trim().min(1).max(2_000),
});
export type InternalAuditDetails = z.infer<typeof auditDetailsSchema>;

export const surveyDetailsSchema = z.object({
  audience: z.string().trim().min(1).max(500),
  responseMode: z.literal('anonymous').default('anonymous'),
  consentStatement: z.string().trim().min(1).max(1_000),
});
export type SurveyDetails = z.infer<typeof surveyDetailsSchema>;

export const surveyTemplateStatusSchema = z.enum(['active', 'archived']);
export type SurveyTemplateStatus = z.infer<typeof surveyTemplateStatusSchema>;

export const surveyTemplateSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(4_000).default(''),
  audience: surveyDetailsSchema.shape.audience,
  consentStatement: surveyDetailsSchema.shape.consentStatement,
  questions: z.array(z.string().trim().min(1).max(500)).min(1).max(20),
  status: surveyTemplateStatusSchema,
  createdBy: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SurveyTemplate = z.infer<typeof surveyTemplateSchema>;

export const createSurveyTemplateSchema = surveyTemplateSchema.pick({
  name: true,
  description: true,
  audience: true,
  consentStatement: true,
  questions: true,
});

export const updateSurveyTemplateSchema = surveyTemplateSchema
  .pick({
    name: true,
    description: true,
    audience: true,
    consentStatement: true,
    questions: true,
    status: true,
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated');

export const qualityItemSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  kind: qualityItemKindSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(4_000).default(''),
  status: qualityItemStatusSchema,
  ownerId: z.string().min(1),
  dueAt: z.string().datetime().optional(),
  planDetails: planDetailsSchema.optional(),
  planApproval: planApprovalSchema.optional(),
  scheduleDetails: scheduleDetailsSchema.optional(),
  auditDetails: auditDetailsSchema.optional(),
  surveyDetails: surveyDetailsSchema.optional(),
  surveyQuestions: z.array(z.string().trim().min(1).max(500)).min(1).max(20).optional(),
  completionSummary: z.string().trim().min(1).max(4_000).optional(),
  createdBy: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type QualityItem = z.infer<typeof qualityItemSchema>;

const createQualityItemFields = {
  title: qualityItemSchema.shape.title,
  description: qualityItemSchema.shape.description,
};

export const createQualityItemSchema = z.discriminatedUnion('kind', [
  z.object({
    ...createQualityItemFields,
    kind: z.literal('plan'),
    dueAt: z.string().datetime(),
    planDetails: planDetailsSchema,
  }),
  z.object({
    ...createQualityItemFields,
    kind: z.literal('schedule'),
    dueAt: z.string().datetime(),
    scheduleDetails: scheduleDetailsSchema,
  }),
  z.object({
    ...createQualityItemFields,
    kind: z.literal('audit'),
    dueAt: z.string().datetime().optional(),
    auditDetails: auditDetailsSchema,
  }),
  z.object({
    ...createQualityItemFields,
    kind: z.literal('survey'),
    dueAt: z.string().datetime().optional(),
    surveyDetails: surveyDetailsSchema,
    surveyQuestions: qualityItemSchema.shape.surveyQuestions.unwrap(),
  }),
]);

export const qualityItemTransitionSchema = z.object({
  from: qualityItemStatusSchema,
  to: qualityItemStatusSchema,
});

export const updateQualityItemSchema = qualityItemSchema
  .pick({
    title: true,
    description: true,
    status: true,
    ownerId: true,
    dueAt: true,
    planDetails: true,
    scheduleDetails: true,
    auditDetails: true,
    surveyDetails: true,
    surveyQuestions: true,
    completionSummary: true,
  })
  .partial()
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated');

export const planActionStatusSchema = z.enum(['open', 'in_progress', 'complete', 'cancelled']);
export type PlanActionStatus = z.infer<typeof planActionStatusSchema>;

export const planActionSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  planId: z.string().min(1),
  description: z.string().trim().min(1).max(2_000),
  ownerId: z.string().min(1),
  dueAt: z.string().datetime().optional(),
  status: planActionStatusSchema,
  evidenceFileIds: z.array(z.string().min(1)).max(20).default([]),
  completionSummary: z.string().trim().min(1).max(4_000).optional(),
  createdBy: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PlanAction = z.infer<typeof planActionSchema>;

export const createPlanActionSchema = planActionSchema.pick({
  description: true,
  dueAt: true,
  evidenceFileIds: true,
});

export const updatePlanActionSchema = planActionSchema
  .pick({
    description: true,
    ownerId: true,
    dueAt: true,
    status: true,
    evidenceFileIds: true,
    completionSummary: true,
  })
  .partial()
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated');

export const auditFindingClassificationSchema = z.enum([
  'conformity',
  'observation',
  'minor_nonconformity',
  'major_nonconformity',
]);
export type AuditFindingClassification = z.infer<typeof auditFindingClassificationSchema>;

export const auditFindingStatusSchema = z.enum(['open', 'in_progress', 'closed']);
export type AuditFindingStatus = z.infer<typeof auditFindingStatusSchema>;

export const auditFindingSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  auditId: z.string().min(1),
  title: z.string().trim().min(1).max(200),
  statement: z.string().trim().min(1).max(4_000),
  classification: auditFindingClassificationSchema,
  correctiveAction: z.string().trim().min(1).max(4_000).optional(),
  ownerId: z.string().min(1),
  dueAt: z.string().datetime().optional(),
  status: auditFindingStatusSchema,
  evidenceFileIds: z.array(z.string().min(1)).max(20).default([]),
  closureEvidenceFileIds: z.array(z.string().min(1)).max(20).default([]),
  closureSummary: z.string().trim().min(1).max(4_000).optional(),
  createdBy: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type AuditFinding = z.infer<typeof auditFindingSchema>;

export const createAuditFindingSchema = auditFindingSchema
  .pick({
    title: true,
    statement: true,
    classification: true,
    correctiveAction: true,
    dueAt: true,
    evidenceFileIds: true,
  })
  .superRefine((value, context) => {
    if (
      (value.classification === 'minor_nonconformity' ||
        value.classification === 'major_nonconformity') &&
      !value.correctiveAction?.trim()
    ) {
      context.addIssue({
        code: 'custom',
        path: ['correctiveAction'],
        message: 'A corrective action is required for a nonconformity',
      });
    }
  });

export const updateAuditFindingSchema = auditFindingSchema
  .pick({
    title: true,
    statement: true,
    classification: true,
    correctiveAction: true,
    dueAt: true,
    status: true,
    evidenceFileIds: true,
    closureEvidenceFileIds: true,
    closureSummary: true,
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated');

export const fileRecordSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  key: z.string().min(1),
  fileName: z.string().min(1).max(200),
  contentType: z.string().min(1).max(150),
  status: z.enum(['pending', 'available', 'quarantined', 'deleted']),
  size: z.number().int().nonnegative().optional(),
  etag: z.string().max(200).optional(),
  createdBy: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type FileRecord = z.infer<typeof fileRecordSchema>;

export const createSurveyTokenSchema = z.object({
  expiresInDays: z.number().int().min(1).max(90).default(30),
  maximumResponses: z.number().int().min(1).max(50_000).default(1),
});

export const surveyResponseSchema = z.object({
  answers: z.array(z.string().trim().max(4_000)).min(1).max(20),
  consent: z.literal(true),
});

export const surveyResponseRecordSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().uuid(),
  surveyId: z.string().min(1),
  answers: surveyResponseSchema.shape.answers,
  consentedAt: z.string().datetime().optional(),
  submittedAt: z.string().datetime(),
});
export type SurveyResponseRecord = z.infer<typeof surveyResponseRecordSchema>;

export const surveyGuestTokenSchema = z.object({
  tokenHash: z.string().length(64),
  tenantId: z.string().uuid(),
  surveyId: z.string().uuid(),
  expiresAt: z.string().datetime(),
  maximumResponses: z.number().int().positive().default(1),
  responseCount: z.number().int().nonnegative().default(0),
  status: z.enum(['active', 'revoked']).default('active'),
  createdBy: z.string().min(1),
  createdAt: z.string().datetime(),
  revokedBy: z.string().min(1).optional(),
  revokedAt: z.string().datetime().optional(),
});

export type SurveyGuestToken = z.infer<typeof surveyGuestTokenSchema>;

export {
  createPlatformTimeLogEntrySchema,
  platformTimeTimerSchema,
  startPlatformTimeTimerSchema,
  platformTimeLogEntrySchema,
  platformTimeLogListSchema,
  updatePlatformTimeLogEntrySchema,
  type CreatePlatformTimeLogEntry,
  type PlatformTimeLogEntry,
  type PlatformTimeTimer,
  type StartPlatformTimeTimer,
  type UpdatePlatformTimeLogEntry,
} from './platform-time-log.js';
